// Sort field dropdown + direction toggle. Collapses to an icon inside
// CollectionToolbar's compact mode when not the expanded side.

import { useMemo } from 'react';
import { ArrowDownUpIcon, ChevronDownIcon } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Toggle } from '../ui/toggle';
import { useCollectionToolbarContext } from './CollectionToolbarContext';
import type { SortDirection } from '../../utilities/sort';

export type SortOption<T extends string> = {
  value: T;
  label: string;
  // Omit `directionLabels` for fields where direction is meaningless (e.g.,
  // shuffle). When the active option is directionless, the toggle is hidden.
  directionLabels?: { asc: string; desc: string };
};

type SortControlProps<T extends string> = {
  options: SortOption<T>[];
  field: T;
  direction: SortDirection;
  onFieldChange: (field: T) => void;
  onDirectionChange: (direction: SortDirection) => void;
};

const SortControl = <T extends string>({
  options,
  field,
  direction,
  onFieldChange,
  onDirectionChange,
}: SortControlProps<T>) => {
  const { isCompact, expanded, setExpanded } = useCollectionToolbarContext();
  const collapsed = isCompact && expanded !== 'sort';

  const activeOption = useMemo(
    () => options.find((option) => option.value === field) ?? options[0],
    [options, field]
  );
  const directionLabel = activeOption.directionLabels?.[direction];

  if (collapsed) {
    return (
      <Button
        variant="outline"
        size="sm"
        type="button"
        aria-label="Sort options"
        onClick={() => setExpanded('sort')}
      >
        <ArrowDownUpIcon />
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" type="button">
            Sort: {activeOption.label}
            <ChevronDownIcon data-icon="inline-end" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuRadioGroup
            value={field}
            onValueChange={(value) => onFieldChange(value as T)}
          >
            {options.map((option) => (
              <DropdownMenuRadioItem key={option.value} value={option.value}>
                {option.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {directionLabel && (
        <Toggle
          aria-label={`Sort direction: ${directionLabel}`}
          pressed={direction === 'desc'}
          onPressedChange={(isPressed) =>
            onDirectionChange(isPressed ? 'desc' : 'asc')
          }
          variant="outline"
          size="sm"
        >
          <ArrowDownUpIcon data-icon="inline-start" />
          {directionLabel}
        </Toggle>
      )}
    </div>
  );
};

export default SortControl;
